// ***********************************************************
// pathname: /v1/job/{jobSlug}/phone-apply/verify
// ***********************************************************
import { useSwrMutationApi } from '@hooks/useSwrApi';
import { useTranslation } from 'next-i18next';

type VerifyWrite = ApiSchema<'PhoneApplyVerifyWrite'>;
type VerifyResponse = ApiSchema<'PhoneApplyVerifyResponse'>;

interface UseSwrVerifyPhoneApplyProps {
  locale: string;
  jobSlug: string;
  onSuccess?: (data: VerifyResponse) => void;
  onError?: () => void;
}

/**
 * @description Verifies the SMS code, registers the application, and
 * returns a one-time auth token plus the applicationOutcome. The
 * caller swaps the auth token for an API session via `phoneOtpAction`.
 *
 * See docs/plans/phone-apply-flow.md.
 */
export const useSwrVerifyPhoneApply = (props: UseSwrVerifyPhoneApplyProps) => {
  const {
    locale, jobSlug, onSuccess, onError,
  } = props;

  const { t } = useTranslation('common');

  return useSwrMutationApi<VerifyResponse, VerifyWrite>({
    url: `/v1/job/${jobSlug}/phone-apply/verify`,
    locale,
    fetcherConfig: {
      method: 'POST',
      stringifyPayload: true,
    },
    apiOptions: {
      onSuccess: (data) => {
        if (onSuccess && data) onSuccess(data);
      },
      onError: () => {
        if (onError) onError();
      },
    },
    toastsOptions: {
      showSuccessToast: false,
      showErrorToast: true,
      byErrorCodes: [
        { code: '422', text: t('phone_apply.error.invalid_code', 'Invalid code. Please try again.') },
        { code: '404', text: t('phone_apply.error.job_not_found', 'Job not found.') },
      ],
    },
  });
};
